import { useState, useEffect } from 'react';
import { Table, Tag, Button, Modal, Form, InputNumber, Input, message, Select, Space, Descriptions, Alert, Tabs } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, DollarOutlined, EyeOutlined } from '@ant-design/icons';
import PageHeader from '../components/PageHeader';
import api, { Contribution, User } from '../api';
import type { ColumnsType } from 'antd/es/table';
import './AdminContributions.css';

const AdminContributions = () => {
  const [activeTab, setActiveTab] = useState<string>('pending');
  const [contributions, setContributions] = useState<Contribution[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedContribution, setSelectedContribution] = useState<Contribution | null>(null);
  const [filterUserId, setFilterUserId] = useState<string | undefined>(undefined);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchContributions();
    fetchUsers();
  }, [activeTab, filterUserId]);

  const fetchContributions = async () => {
    setLoading(true);
    try {
      const params: any = {};
      // Set status based on active tab
      if (activeTab === 'pending') {
        params.status = 'pending';
      } else {
        // For processed tab, don't filter by status to get both confirmed and failed
      }
      if (filterUserId) params.user_id = filterUserId;
      
      const data = await api.contributions.admin.list(params);
      
      // Filter processed contributions client-side if needed
      if (activeTab === 'processed') {
        setContributions(data.filter((c: Contribution) => c.status !== 'pending'));
      } else {
        setContributions(data);
      }
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Failed to fetch contributions');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const data = await api.users.admin.list();
      setUsers(data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const handleCreate = async (values: any) => {
    try {
      await api.contributions.admin.create({
        user_id: values.user_id,
        amount_cents: Math.round(values.amount_dollars * 100),
        note: values.note
      });
      message.success('Contribution created successfully');
      setCreateModalOpen(false);
      form.resetFields();
      fetchContributions();
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Failed to create contribution');
    }
  };

  const handleConfirm = async (id: string) => {
    try {
      await api.contributions.admin.confirm(id);
      message.success('Contribution confirmed! User base principal initialized and now eligible for earnings.');
      fetchContributions();
      fetchUsers(); // Refresh users list to show updated eligibility
      if (selectedContribution?.id === id) {
        setDetailModalOpen(false);
      }
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Failed to confirm contribution');
    }
  };

  const handleReject = async (id: string, reason: string) => {
    try {
      await api.contributions.admin.reject(id, { reason });
      message.success('Contribution rejected');
      fetchContributions();
      if (selectedContribution?.id === id) {
        setDetailModalOpen(false);
      }
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Failed to reject contribution');
    }
  };

  const showRejectModal = (contribution: Contribution) => {
    Modal.confirm({
      title: 'Reject Contribution',
      content: (
        <Form>
          <Form.Item label="Reason for rejection">
            <Input.TextArea 
              id="reject-reason"
              rows={3}
              placeholder="Enter reason for rejection..."
            />
          </Form.Item>
        </Form>
      ),
      onOk: () => {
        const reason = (document.getElementById('reject-reason') as HTMLTextAreaElement)?.value || 'Rejected by admin';
        return handleReject(contribution.id, reason);
      },
      okText: 'Reject',
      okButtonProps: { danger: true }
    });
  };

  const formatCurrency = (cents: number) => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  const columns: ColumnsType<Contribution> = [
    {
      title: 'User',
      dataIndex: ['user', 'full_name'],
      key: 'user',
      render: (text: string, record: Contribution) => (
        <div>
          <div>{text}</div>
          <div style={{ fontSize: '12px', color: '#888' }}>{record.user?.email}</div>
        </div>
      )
    },
    {
      title: 'Amount',
      dataIndex: 'amount_cents',
      key: 'amount',
      render: (cents: number) => formatCurrency(cents),
      sorter: (a, b) => a.amount_cents - b.amount_cents
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string, record: Contribution) => {
        const color = status === 'confirmed' ? 'green' : status === 'failed' ? 'red' : 'gold';
        return (
          <div>
            <Tag color={color}>{status.toUpperCase()}</Tag>
            {status === 'pending' && record.meets_minimum !== undefined && (
              <Tag color={record.meets_minimum ? 'blue' : 'orange'}>
                {record.meets_minimum ? '≥ MIN' : '< MIN'}
              </Tag>
            )}
          </div>
        );
      },
      filters: [
        { text: 'Pending', value: 'pending' },
        { text: 'Confirmed', value: 'confirmed' },
        { text: 'Failed', value: 'failed' }
      ]
    },
    {
      title: 'Eligibility',
      key: 'eligibility',
      render: (_: any, record: Contribution) => {
        if (record.status !== 'confirmed' || !record.user) return '-';
        return (
          <Tag color={record.user.eligible_for_earnings ? 'green' : 'orange'}>
            {record.user.eligibility_status.toUpperCase()}
          </Tag>
        );
      }
    },
    {
      title: 'Date',
      dataIndex: 'created_at',
      key: 'date',
      render: (date: string) => new Date(date).toLocaleDateString(),
      sorter: (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: Contribution) => (
        <Space>
          <Button
            size="small"
            icon={<EyeOutlined />}
            onClick={() => {
              setSelectedContribution(record);
              setDetailModalOpen(true);
            }}
          >
            View
          </Button>
          {record.status === 'pending' && (
            <>
              <Button
                size="small"
                type="primary"
                icon={<CheckCircleOutlined />}
                onClick={() => handleConfirm(record.id)}
              >
                Confirm
              </Button>
              <Button
                size="small"
                danger
                icon={<CloseCircleOutlined />}
                onClick={() => showRejectModal(record)}
              >
                Reject
              </Button>
            </>
          )}
        </Space>
      )
    }
  ];

  return (
    <div>
      <PageHeader 
        title="Contributions" 
        extra={
          <Button
            type="primary"
            icon={<DollarOutlined />}
            onClick={() => setCreateModalOpen(true)}
          >
            Record Contribution
          </Button>
        }
      />

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: 'pending',
            label: 'Open Contributions',
            children: (
              <>
                <div style={{ marginBottom: 16 }}>
                  <Select
                    style={{ width: 200 }}
                    placeholder="Filter by user"
                    allowClear
                    showSearch
                    filterOption={(input, option) =>
                      (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                    value={filterUserId}
                    onChange={setFilterUserId}
                    options={users.map(u => ({ label: `${u.full_name} (${u.email})`, value: u.id }))}
                  />
                </div>
                <Table
                  columns={columns}
                  dataSource={contributions}
                  rowKey="id"
                  loading={loading}
                />
              </>
            )
          },
          {
            key: 'processed',
            label: 'Processed Contributions',
            children: (
              <>
                <div style={{ marginBottom: 16 }}>
                  <Select
                    style={{ width: 200 }}
                    placeholder="Filter by user"
                    allowClear
                    showSearch
                    filterOption={(input, option) =>
                      (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                    value={filterUserId}
                    onChange={setFilterUserId}
                    options={users.map(u => ({ label: `${u.full_name} (${u.email})`, value: u.id }))}
                  />
                </div>
                <Table
                  columns={columns}
                  dataSource={contributions}
                  rowKey="id"
                  loading={loading}
                />
              </>
            )
          }
        ]}
      />

      {/* Create Modal */}
      <Modal
        title="Record New Contribution"
        open={createModalOpen}
        onCancel={() => {
          setCreateModalOpen(false);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        okText="Create"
        styles={{
          body: { backgroundColor: '#141414' },
          header: { backgroundColor: '#141414' },
          footer: { backgroundColor: '#141414' }
        }}
      >
        <Form form={form} onFinish={handleCreate} layout="vertical">
          <Form.Item
            name="user_id"
            label="User"
            rules={[{ required: true, message: 'Please select a user' }]}
          >
            <Select
              showSearch
              placeholder="Select user"
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={users.map(u => ({
                label: `${u.full_name} (${u.email}) - Min: ${formatCurrency(u.min_required_contribution_cents)}`,
                value: u.id
              }))}
              popupClassName="opaque-dropdown"
            />
          </Form.Item>
          <Form.Item
            name="amount_dollars"
            label="Amount (USD)"
            rules={[
              { required: true, message: 'Please enter an amount' },
              { type: 'number', min: 0.01, message: 'Amount must be greater than $0' }
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              prefix="$"
              precision={2}
              placeholder="0.00"
            />
          </Form.Item>
          <Form.Item name="note" label="Note">
            <Input.TextArea rows={3} placeholder="Optional note..." />
          </Form.Item>
        </Form>
      </Modal>

      {/* Detail Modal */}
      <Modal
        title="Contribution Details"
        open={detailModalOpen}
        onCancel={() => setDetailModalOpen(false)}
        width={700}
        styles={{
          body: { backgroundColor: '#141414' },
          header: { backgroundColor: '#141414' },
          footer: { backgroundColor: '#141414' }
        }}
        footer={
          selectedContribution?.status === 'pending' ? [
            <Button key="cancel" onClick={() => setDetailModalOpen(false)}>
              Close
            </Button>,
            <Button
              key="reject"
              danger
              icon={<CloseCircleOutlined />}
              onClick={() => selectedContribution && showRejectModal(selectedContribution)}
            >
              Reject
            </Button>,
            <Button
              key="confirm"
              type="primary"
              icon={<CheckCircleOutlined />}
              onClick={() => selectedContribution && handleConfirm(selectedContribution.id)}
            >
              Confirm
            </Button>
          ] : [
            <Button key="close" onClick={() => setDetailModalOpen(false)}>
              Close
            </Button>
          ]
        }
      >
        {selectedContribution && (
          <>
            {selectedContribution.status === 'pending' && !selectedContribution.meets_minimum && (
              <Alert
                title="Below Minimum Requirement"
                description={`This contribution is ${formatCurrency(selectedContribution.shortfall_cents || 0)} below the required minimum. User will not be eligible for earnings if confirmed at this amount.`}
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
              />
            )}
            
            <Descriptions column={1} bordered>
              <Descriptions.Item label="User">
                <div>
                  <div><strong>{selectedContribution.user?.full_name}</strong></div>
                  <div style={{ color: '#888' }}>{selectedContribution.user?.email}</div>
                </div>
              </Descriptions.Item>
              <Descriptions.Item label="Amount">
                {formatCurrency(selectedContribution.amount_cents)}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={
                  selectedContribution.status === 'confirmed' ? 'green' : 
                  selectedContribution.status === 'failed' ? 'red' : 'gold'
                }>
                  {selectedContribution.status.toUpperCase()}
                </Tag>
              </Descriptions.Item>
              {selectedContribution.user && (
                <>
                  <Descriptions.Item label="Required Minimum">
                    {formatCurrency(selectedContribution.user.min_required_contribution_cents)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Meets Minimum">
                    {selectedContribution.meets_minimum !== undefined ? (
                      <Tag color={selectedContribution.meets_minimum ? 'blue' : 'orange'}>
                        {selectedContribution.meets_minimum ? 'YES' : 'NO'}
                      </Tag>
                    ) : '-'}
                  </Descriptions.Item>
                  {selectedContribution.status === 'confirmed' && (
                    <Descriptions.Item label="Earning Eligibility">
                      <Tag color={selectedContribution.user.eligible_for_earnings ? 'green' : 'orange'}>
                        {selectedContribution.user.eligibility_status.toUpperCase()}
                      </Tag>
                    </Descriptions.Item>
                  )}
                </>
              )}
              <Descriptions.Item label="Created">
                {new Date(selectedContribution.created_at).toLocaleString()}
              </Descriptions.Item>
              {selectedContribution.confirmed_at && (
                <Descriptions.Item label="Confirmed">
                  {new Date(selectedContribution.confirmed_at).toLocaleString()}
                </Descriptions.Item>
              )}
              {selectedContribution.note && (
                <Descriptions.Item label="Note">
                  {selectedContribution.note}
                </Descriptions.Item>
              )}
            </Descriptions>
          </>
        )}
      </Modal>
    </div>
  );
};

export default AdminContributions;
